/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ProductCardPromotionBadges, useCartContext } from "@farmlands/commerce";
import { colors } from "@farmlands/mui-theme";
import { Box, Divider, Link, styled } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Button } from "../../../../components/Button";
import Counter from "../../../../components/Counter";
import { ImageSlider } from "../../../../components/ImageSlider";
import ToastIcon from "../../../../components/ToastIcon";
import Typography from "../../../../components/Typography";
import { usePermission } from "../../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../../services/firebase/remote-config/remoteConfig.slice";
import { useAppDispatch } from "../../../../store/rootStore";
import TestIds from "../../../../testIds";
import { URLS } from "../../../../util/consts";
import { useBranch } from "../../../../util/useBranch";
import { selectCanUseQuickOrder, selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import CartSlice from "../../../cart/storage/cart.slice";
import { CartItem } from "../../../cart/storage/cart.types";
import { getUpdatedVariant } from "../../../cart/utils/getUpdatedVariant";
import { CostPriceList } from "../../../cost-price/components/CostPriceList";
import RequestBulkHelperMessage from "../../../quotes/components/HelperMessage/RequestBulkHelperMessage";
import { QuoteModalType } from "../../../quotes/components/QuoteModal/types";
import { useQuoteModal } from "../../../quotes/components/QuoteModal/useQuoteModal";
import {
  getStockAvailabilities,
  MediaLocationType,
  Product,
  ProductDetailsApiResponse,
  Variant,
} from "../../storage";
import { selectProduct, StocksSlice } from "../../storage/stocks.slice";
import { getProductPrices } from "../../utils/getPrices";
import { getPromotionBadges } from "../../utils/getPromotionBadges";
import { useProductStatus } from "../../utils/useProductStatus";
import { ProductPriceDetails } from "../ProductCard/ProductPriceDetails";
import { ProductVariantDetails } from "../ProductCard/ProductVariantDetails";
import { StockAvailability } from "../StockAvailability/StockAvailability";

const InfoContainer = styled("div")(({ theme }) => ({
  display: "flex",
  background: colors.shade0,
  padding: "32px",
  gap: "24px",
  border: `1px solid ${colors.primary100}`,
  marginTop: "24px",
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    padding: "16px",
    minWidth: "unset",
  },
}));

const FlexRowBox = styled(Box)({
  display: "flex",
  width: "100%",
});

const FlexColBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const HelperMessageContainer = styled("div")(({ theme }) => ({
  background: colors.primary100,
  [theme.breakpoints.down("lg")]: {
    margin: "0 -16px -16px -16px",
  },
}));

export interface QueryVariant {
  size: string;
  colour: string;
  qty: number;
}

export const safeQuantity = (value: string | null) => {
  return parseInt(value ?? "1");
};
interface ProductInfoProps {
  item: ProductDetailsApiResponse;
  queryVariant: QueryVariant;
  setQueryVariant: React.Dispatch<React.SetStateAction<QueryVariant>>;
}

export const ProductInfo = ({ item, queryVariant, setQueryVariant }: ProductInfoProps) => {
  const dispatch = useAppDispatch();
  const { addToCart } = useCartContext();
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const hasCostPricePermission = usePermission("StaffSpecific_Pricing_CostPriceView").allowed;
  const [currentProductId, setCurrentProductId] = useState(item.productId || item.masterProductId);
  const remoteConfig = useSelector(selectRemoteConfig);

  const product = useSelector(selectProduct(currentProductId));
  const isTfo = selectedAccount?.hasTfoAccess;
  const quoteProduct = useQuoteModal();
  const canUseQuickOrder = useSelector(selectCanUseQuickOrder);
  const cartOrderPermission = usePermission("FarmlandsProShop_CartOrderCreate");
  const canUseCart = canUseQuickOrder && cartOrderPermission.allowed;
  const branchId = useBranch();

  React.useEffect(() => {
    const cartItem = {
      ...item,
      productId: currentProductId,
      quantity: queryVariant.qty,
      description: item.name,
      imageUrl:
        item.mediaLocations && item.mediaLocations.length > 0
          ? transformImageUrl(item.mediaLocations)[0]
          : "",
    } as CartItem;

    dispatch(StocksSlice.actions.addStock(cartItem));

    const getStocks = async () => {
      const items = await getStockAvailabilities({
        productIds:
          item.variants?.map((v) => v.productId).length === 0
            ? [item.masterProductId]
            : item.variants?.map((v) => v.productId),
      });
      if (items && items.length > 0) {
        dispatch(StocksSlice.actions.addAvailabilities(items));
      }
    };

    getStocks();
  }, [item]);

  const onChangeVariant = useCallback(
    (variant: Variant) => {
      const updatedVariant = getUpdatedVariant(variant, {
        ...product,
        quantity: queryVariant.qty,
      } as CartItem);
      setCurrentProductId(updatedVariant.productId);
      dispatch(StocksSlice.actions.addStock(updatedVariant));
      setQueryVariant({
        ...queryVariant,
        size: updatedVariant.sizeDescription ?? "",
        colour: updatedVariant.colourDescription ?? "",
      });
    },
    [product, queryVariant, dispatch, setQueryVariant]
  );

  const handleAddToCart = () => {
    if (!product || !selectedAccount) {
      return;
    }

    const imageUrl =
      item.mediaLocations && item.mediaLocations.length > 0
        ? transformImageUrl(item.mediaLocations)[0]
        : undefined;

    dispatch(
      CartSlice.actions.addToCart({
        accountNumber: selectedAccount.accountNumber,
        product: {
          ...product,
          quantity: queryVariant.qty,
          description: item.name,
          basePriceExcGst: item.basePriceExcGst,
          basePriceIncGst: item.basePriceIncGst,
          discountedPriceExcGst: item.discountedPriceExcGst,
          discountedPriceIncGst: item.discountedPriceIncGst,
        },
      })
    );
    addToCart({
      productId: currentProductId,
      masterProductId: item.masterProductId,
      name: item.name,
      sku: item.itemNumber,
      quantity: queryVariant.qty,
      imageUrl,
      imageAlt: item.name,
      size: queryVariant.size || item.sizeDescription || "",
      colour: queryVariant.colour || item.colourDescription || "",
      basePriceExcGst: item.basePriceExcGst,
      discountedPriceExcGst: item.discountedPriceExcGst,
      discountedPriceIncGst: item.discountedPriceIncGst,
    });

    toast("Item added to cart", {
      type: "success",
      icon: <ToastIcon>check_circle</ToastIcon>,
    });
  };

  const transformImageUrl = (images: MediaLocationType[]) => {
    return images?.map((x) => `${URLS.imageBase}${x.uri}`);
  };

  const renderBasicInfo = React.useMemo(() => {
    return (
      <FlexColBox gap={1} data-testid={TestIds.feat.product.details.descriptionContainer}>
        <Typography variant="h2" color="grey800" data-testid={TestIds.feat.product.card.name}>
          {item.name}
        </Typography>
        <Typography variant="body2" color="grey700" data-testid={TestIds.feat.product.card.SKU}>
          {`SKU: ${item.itemNumber}`}
        </Typography>
        <StockAvailability productId={currentProductId} showStore />
        <Divider sx={{ mt: 0.5, mb: 0.5 }} />
        <Typography
          variant="body2"
          color="grey700"
          data-testid={TestIds.feat.product.details.shortDescription}
        >
          {item.onlineShortDescription}
        </Typography>
      </FlexColBox>
    );
  }, [
    item.name,
    item.itemNumber,
    item.onlineShortDescription,
    product,
    currentProductId,
    branchId,
  ]);

  const renderPromotionBadges = React.useMemo(() => {
    const badges = getPromotionBadges(
      item.promotions,
      item.priceBreaks,
      remoteConfig.traderDealsFilterValue,
      remoteConfig.traderDealsEnabled,
      remoteConfig.buyBreaksEnabled
    ).map((badge) => badge.label);
    if (badges.length === 0) {
      return null;
    }
    return (
      <div
        data-testid={TestIds.feat.product.details.imageBadgeContainer}
        className="absolute top-0 left-0 z-10"
      >
        <ProductCardPromotionBadges labels={badges} />
      </div>
    );
  }, [
    item.promotions,
    remoteConfig.traderDealsFilterValue,
    remoteConfig.traderDealsEnabled,
    remoteConfig.buyBreaksEnabled,
  ]);

  const renderPrices = React.useMemo(() => {
    return <ProductPriceDetails items={getProductPrices({ item })} size="Large" />;
  }, [item.discountedPriceExcGst, item.basePriceExcGst]);

  const { canPurchase } = useProductStatus(item.lifeCycleState, item.basePriceExcGst);

  const renderVariants = React.useMemo(() => {
    if (product && item.variants && item.variants.length > 0) {
      return (
        <ProductVariantDetails
          key={"variants"}
          item={product}
          enableVariants
          onChangeVariant={onChangeVariant}
        />
      );
    }
  }, [product, item.variants, onChangeVariant]);

  const renderButtons = React.useMemo(() => {
    if (canUseCart && canPurchase) {
      if (!isTfo && product?.stockAvailability === "Enquire") {
        return (
          <FlexRowBox gap={2}>
            <Button
              key="enquire-button"
              variant="contained"
              onClick={() => {
                quoteProduct.setQuoteType(QuoteModalType.ENQUIRY);
                quoteProduct.setProduct(product);
              }}
              sx={{ mt: "auto", transition: "all 0.5s ease", whiteSpace: "nowrap", width: "100%" }}
              data-testid={TestIds.feat.product.details.enquireButton}
            >
              Enquire
            </Button>
          </FlexRowBox>
        );
      }

      return (
        <FlexRowBox gap={2}>
          <Counter
            value={queryVariant.qty}
            onChange={(value) => {
              if (value) {
                setQueryVariant({
                  ...queryVariant,
                  qty: value,
                });
              }
            }}
          />
          <Button
            key="add-to-cart-button"
            variant="contained"
            onClick={handleAddToCart}
            sx={{ mt: "auto", transition: "all 0.5s ease", whiteSpace: "nowrap", width: "100%" }}
            data-testid={TestIds.feat.product.details.orderButton}
          >
            Add to cart
          </Button>
        </FlexRowBox>
      );
    }
    return null;
  }, [product, queryVariant, canUseCart, canPurchase]);

  const renderHelpMessage = React.useMemo(() => {
    if (item.isPriceNegotiable && quoteProduct.canRequestQuote && !isTfo) {
      return (
        <HelperMessageContainer>
          <RequestBulkHelperMessage show />
          <Link
            component="button"
            onClick={() => {
              quoteProduct.setQuoteType(QuoteModalType.QUOTE_SINGLE);
              quoteProduct.setProduct(product as Product);
            }}
            sx={{ mb: 1, ml: 2, p: 0 }}
            data-testid={TestIds.feat.product.details.requestQuoteButton}
          >
            <Typography variant="body2" color="primary700">
              Request a quote
            </Typography>
          </Link>
        </HelperMessageContainer>
      );
    }
  }, [product]);

  const ImageSliderMemo = React.useMemo(() => {
    return <ImageSlider images={transformImageUrl(item.mediaLocations ?? [])} />;
  }, [item.mediaLocations]);
  return (
    <InfoContainer>
      <FlexRowBox flex={6} justifyContent={"center"} sx={{ position: "relative" }}>
        {renderPromotionBadges}
        {ImageSliderMemo}
      </FlexRowBox>
      <FlexColBox flex={4} gap={4} data-testid={TestIds.feat.product.details.container}>
        {renderBasicInfo}
        {renderPrices}
        {renderVariants}
        {renderButtons}
        {renderHelpMessage}
        {remoteConfig.costPriceEnabled && hasCostPricePermission && (
          <CostPriceList productId={currentProductId} />
        )}
      </FlexColBox>
    </InfoContainer>
  );
};
